import { createFileRoute, Outlet, redirect } from '@tanstack/react-router';
import { auth, useAuth } from '@/lib/platform';
import { ToastProvider } from '@/components/ui/toast';
import { BeeLoader } from '@/components/ui/bee-loader';
import { Sidebar } from '@/components/chrome/sidebar';
import { AccountProvider, useAccount } from '@/components/chrome/account-context';
import { CapabilitiesProvider } from '@/components/chrome/capabilities-context';
import { signOutUser } from '@/lib/platform';

/**
 * Roles allowed to use this tool. Mirrors webadmin's gate.
 *
 * ⚠ THIS IS A UI GATE ONLY — it is not authoritative. Transit's tenancy
 * middleware checks role/Platform for /admin/* only
 * (internal/shared/tenancy/tenantauth/middleware.go), so ANY valid token from the
 * transit Firebase project already has full rider access to /api/v1/*. Google
 * sign-up is open on those projects, so this gate keeps casual accounts out of the
 * UI but cannot stop a direct API call. The durable fix is a server-side allowlist
 * or a GCIP blocking function restricting sign-up to the org domain.
 */
const ALLOWED_ROLES = new Set(['admin', 'editor']);

export const Route = createFileRoute('/_authed')({
  beforeLoad: async () => {
    await auth.authStateReady();
    if (!auth.currentUser) {
      throw redirect({ to: '/login' });
    }
  },
  component: AuthedLayout,
});

function Shell() {
  const { open } = useAccount();
  const user = auth.currentUser;
  // Identity comes straight from the Google account — there is no profile lookup
  // any more (that was weebee's /api/v1/users/me, which this app no longer calls).
  return (
    <div className="wb-shell">
      <Sidebar
        userName={user?.displayName ?? undefined}
        userSubtitle={user?.email ?? undefined}
        onOpenAccount={open}
      />
      <div className="wb-shell-content">
        <Outlet />
      </div>
    </div>
  );
}

function NotAuthorized({ email }: { email: string | null }) {
  return (
    <div className="wb-shell wb-shell--auth">
      <div
        className="wb-auth-card wb-auth-card--wide wb-card wb-card--pad wb-col wb-gap-12"
        style={{ textAlign: 'center' }}
      >
        <h1 className="wb-h2">Not authorized</h1>
        <p className="wb-sub">
          {email ?? 'This account'} is signed in but has no access to the booking tool. An operator
          role must be granted first:
        </p>
        <p className="wb-tiny wb-mono">scripts/set-admin-claim.sh &lt;env&gt; &lt;email&gt; editor</p>
        <button className="wb-btn" onClick={() => void signOutUser()}>
          Sign out
        </button>
      </div>
    </div>
  );
}

function AuthedLayout() {
  const authState = useAuth();

  if (authState.status === 'loading') {
    return (
      <div className="wb-shell wb-center">
        <BeeLoader size={64} />
      </div>
    );
  }

  if (authState.status !== 'authenticated') {
    return null;
  }

  if (!ALLOWED_ROLES.has(authState.role)) {
    return <NotAuthorized email={authState.user.email} />;
  }

  return (
    <ToastProvider>
      <CapabilitiesProvider>
        <AccountProvider>
          <Shell />
        </AccountProvider>
      </CapabilitiesProvider>
    </ToastProvider>
  );
}
